import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  ListItem,
  ListItemButton,
  Snackbar,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useIsFetching } from "react-query";
import { getShiftIcon } from "../utils/getShiftIcon";

import { cacheKeys, useRequestShift } from "../api";

const LinearBuffer = () => {
  const [progress, setProgress] = useState(0);
  const [buffer, setBuffer] = useState(10);

  const progressRef = useRef(() => {});
  useEffect(() => {
    progressRef.current = () => {
      if (progress > 100) {
        setProgress(0);
        setBuffer(10);
      } else {
        const diff = Math.random() * 3;
        const diff2 = Math.random() * 3;
        setProgress(progress + diff);
        setBuffer(progress + diff + diff2);
      }
    };
  });

  useEffect(() => {
    const timer = setInterval(() => {
      progressRef.current();
    }, 500);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <div className="w-full">
      <LinearProgress variant="buffer" value={progress} valueBuffer={buffer} />
    </div>
  );
};

const ShiftDayListItem = ({
  shiftGroup = {},
  showDate = false,
  weekday,
  dayOfMonth,
  selectedScheduleType,
  date,
  setIsOpen,
}) => {
  const { mutateAsync: requestShift, isLoading: requestShiftLoading } =
    useRequestShift();
  const shiftsFetching = Boolean(useIsFetching(cacheKeys.shifts));
  const loading = shiftsFetching || requestShiftLoading;
  const {
    contractLocationName,
    displayDate,
    distance,
    endTime,
    startTime,
    shifts = [],
  } = shiftGroup;
  const isAutoassignable =
    shifts.filter((shift) => shift.isAutoassignable).length > 0;
  const firstShift = shifts[0];
  const [dialogOpen, setDialogOpen] = useState(false);
  const [successAlertOpen, setSuccessAlertOpen] = useState(false);
  const [errorAlertOpen, setErrorAlertOpen] = useState(false);

  return (
    <ListItem divider>
      <ListItemButton disableRipple disableTouchRipple>
        <div className="w-full">
          <div className="mb-2 mt-4 flex items-center gap-2">
            <strong>
              {contractLocationName} {showDate && `(${displayDate})`}
            </strong>
            {isAutoassignable && (
              <LocalFireDepartmentIcon
                sx={{ color: "red", verticalAlign: "middle" }}
              />
            )}
          </div>

          <div className="flex items-center gap-2 mb-2">
            <span className="min-w-[100px]">Position:</span>
            <span>{firstShift.clientJobTitle}</span>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <span className="min-w-[100px]">Shift:</span>
            <span>
              {startTime}-{endTime}
            </span>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <span className="min-w-[100px]">Distance:</span>
            <span>
              {`${distance === null || distance > 100 ? "+100" : Math.round(distance)} miles`}
            </span>
          </div>

          <div className="mt-6 mb-4 flex items-center justify-end ">
            <Button
              variant="contained"
              onClick={() => setDialogOpen(true)}
              className="
                    !bg-[var(--color-blue-light)]
                  !text-white
                    !rounded-2xl
                    !w-36
                    !shadow-none
                    "
            >
              REQUEST
            </Button>
          </div>
        </div>
        <Dialog fullWidth={true} maxWidth={"xs"} open={dialogOpen}>
          <DialogTitle>
            <div className="flex flex-col gap-1">
              <span className="block whitespace-nowrap text-[18px] font-semibold text-[var(--color-black)]">
                Proceed with requesting shift
              </span>

              <div className="flex items-center  justify-center gap-2 text-[14px] text-[var(--color-black)] font-normal bg-gray-100">
                <span>
                  {date
                    ? format(parseISO(date), "EEEE do")
                    : `${weekday} ${dayOfMonth}`}
                </span>
                <span>|</span>
                <span>{format(parseISO(date), "MMMM")}</span>

                <div className="flex items-center gap-1">
                  {getShiftIcon(selectedScheduleType)}
                  <span>{selectedScheduleType}</span>
                </div>
              </div>
            </div>
          </DialogTitle>
          <DialogContent>
            <div className="w-full">
              <div className="mb-2 flex items-center gap-2">
                <strong>
                  {contractLocationName} {showDate && `(${displayDate})`}
                </strong>
                {isAutoassignable && (
                  <LocalFireDepartmentIcon
                    sx={{ color: "red", verticalAlign: "middle" }}
                  />
                )}
              </div>

              <div className="flex gap-2">
                <span className="min-w-[100px] shrink-0">Position:</span>
                <span className="break-words">{firstShift.clientJobTitle}</span>
              </div>

              <div className="flex items-center gap-2 mb-2">
                <span className="min-w-[100px]">Shift:</span>
                <span>
                  {startTime}-{endTime}
                </span>
              </div>

              <div className="flex items-center gap-2 mb-2">
                <span className="min-w-[100px]">Distance:</span>
                <span>
                  {`${distance === null || distance > 100 ? "+100" : Math.round(distance)} miles`}
                </span>
              </div>

              <div className="mt-3">{loading && <LinearBuffer />}</div>
            </div>
          </DialogContent>
          <DialogActions>
            <div className="flex w-full items-center justify-end gap-2 mb-2">
              <Button
                variant="outlined"
                onClick={() => setDialogOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    await requestShift(shiftGroup);
                    setIsOpen(false);
                    setSuccessAlertOpen(true);

                  } catch (error) {
                    setErrorAlertOpen(true);
                  }
                  setDialogOpen(false);
                }}
                disabled={loading}
                autoFocus
              >
                Confirm
              </Button>
            </div>
          </DialogActions>
        </Dialog>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={successAlertOpen}
          onClose={() => setSuccessAlertOpen(false)}
        >
          <Alert variant="filled" severity="success">
            You're Scheduled!
          </Alert>
        </Snackbar>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={errorAlertOpen}
          onClose={() => setErrorAlertOpen(false)}
        >
          <Alert variant="filled" severity="error">
            Error Scheduling Shift
          </Alert>
        </Snackbar>
      </ListItemButton>
    </ListItem>
  );
};

export { ShiftDayListItem };
